import { rgb } from 'pdf-lib';
import moment from 'moment-timezone';
import { TIME_ZONE } from '@silvergatedelivery/constants';
import { getShortIDFromUUID } from 'utilities/format';
import { getDeliveryGroupsByClientByIsActive } from 'graphql/queries';
import cache from 'utilities/cache';

export const receiptPrinterDefaultFields = [
  {
    id: 'date',
    x: 2,
    y: 4,
    fontSize: 8,
    bold: false,
    visible: true,
    allowBold: false,
  },
  {
    id: 'orderShortId',
    x: 35,
    y: 4,
    fontSize: 8,
    bold: false,
    visible: true,
    allowBold: false,
  },
  {
    id: 'deliveryGroupId',
    x: 50,
    y: 4,
    fontSize: 8,
    bold: false,
    visible: true,
    allowBold: false,
  },
  {
    id: 'name',
    x: 5,
    y: 12,
    fontSize: 15,
    bold: true,
    visible: true,
    allowBold: true,
  },
  {
    id: 'tags',
    x: 8,
    y: 18,
    fontSize: 10,
    bold: false,
    visible: true,
    allowBold: false,
  },
  {
    id: 'noteForMeal',
    x: 8,
    y: 24,
    fontSize: 10,
    bold: false,
    visible: true,
    allowBold: false,
  },
  {
    id: 'meals',
    x: 5,
    y: 32,
    fontSize: 14,
    bold: false,
    visible: true,
    allowBold: false,
  },
  {
    id: 'mealNotes',
    x: 40,
    y: 32,
    fontSize: 10,
    bold: false,
    visible: true,
    allowBold: false,
  },
  {
    id: 'custom',
    x: 5,
    y: 40,
    fontSize: 10,
    bold: false,
    visible: true,
    allowBold: false,
  },
];

export const receiptPrinterPageSizeList = [
  {
    width: 80,
    height: 50,
    models: 'DaiDai CT-428D',
  },
  {
    width: 30,
    height: 20,
    models: 'DaiDai CT-428D',
  },
  {
    width: 48,
    height: 100,
    models: 'DaiDai XP-Q90EC',
  },
];

const mmToPoints = (mm) => mm * 72 / 25.4;
const pointsToMM = (points) => points * 25.4 / 72;

export const drawReceiptSection = async (page, order, height, options = {}) => {
  const {
    regularFont,
    boldFont,
    tags = [],
    deliveryGroups = [],
    pageWidth,
    fields,
  } = options;

  if (!fields) return;

  const drawText = (text, size, font, x, y) => {
    const allChars = text.split('');
    const xInPoints = mmToPoints(x);
    let yInPoints = mmToPoints(y);
    let currentLine = '';
    const fontHeight = font.heightAtSize(size);
    for (const char of allChars) {
      const testLine = currentLine ? `${currentLine}${char}` : char;
      const testWidth = font.widthOfTextAtSize(testLine, size);
      if (testWidth > pageWidth - 10) { // 10 points margin
        page.drawText(currentLine, {
          x: xInPoints,
          y: height - yInPoints,
          size,
          color: rgb(0, 0, 0),
          font,
        });
        currentLine = char;
        yInPoints += fontHeight;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) {
      page.drawText(currentLine, {
        x: xInPoints,
        y: height - yInPoints,
        size,
        color: rgb(0, 0, 0),
        font,
      });
    }
  };

  const { elder: { name, tags: { items: elderTags = [] } }, mealItems, deliveryBy } = order;

  // 日期 時段
  const dateField = fields.find((f) => f.id === 'date');
  if (dateField.visible) {
    const mealSlot = order.mealSlot === 'lunch' ? '午餐' : '晚餐';
    const text = `${moment(deliveryBy).tz(TIME_ZONE).format('M/D dddd HH:mm')}  ${mealSlot}`;
    drawText(text, dateField.fontSize, regularFont, dateField.x, dateField.y);
  }

  // 取餐編號
  const orderShortIdField = fields.find((f) => f.id === 'orderShortId');
  if (orderShortIdField.visible) {
    drawText(getShortIDFromUUID(order.id), orderShortIdField.fontSize, regularFont, orderShortIdField.x, orderShortIdField.y);
  }

  // 送餐群組
  const deliveryGroupIdField = fields.find((f) => f.id === 'deliveryGroupId');
  if (deliveryGroupIdField.visible) {
    const deliveryGroup = deliveryGroups.find((g) => g.id === order.deliveryGroupId);
    if (deliveryGroup?.name) {
      drawText(deliveryGroup.name, deliveryGroupIdField.fontSize, regularFont, deliveryGroupIdField.x, deliveryGroupIdField.y);
    }
  }

  // 姓名
  const nameField = fields.find((f) => f.id === 'name');
  if (nameField.visible) {
    drawText(name, nameField.fontSize, nameField.bold ? boldFont : regularFont, nameField.x, nameField.y);
  }

  // 飲食標籤
  const tagsField = fields.find((f) => f.id === 'tags');
  if (tagsField.visible) {
    const tagString = elderTags.map(({ tagId }) => {
      const tagData = tags.find((t) => t.id === tagId);
      return tagData?.label;
    }).filter(Boolean).join('、');
    if (tagString) {
      drawText(tagString, tagsField.fontSize, regularFont, tagsField.x, tagsField.y);
    }
  }

  // 餐點備註
  const noteForMealField = fields.find((f) => f.id === 'noteForMeal');
  if (noteForMealField.visible && order.noteForMeal) {
    drawText(order.noteForMeal, noteForMealField.fontSize, regularFont, noteForMealField.x, noteForMealField.y);
  }

  // 餐點 // TODO: 如果多餐點是要多張還是同一張？
  const mealsField = fields.find((f) => f.id === 'meals');
  if (mealsField.visible && mealItems.length > 0) {
    const fontHeightInMM = pointsToMM(regularFont.heightAtSize(mealsField.fontSize));
    mealItems.forEach((item, index) => {
      const text = `${item.name}  ${item.quantity}份`;
      drawText(text, mealsField.fontSize, regularFont, mealsField.x, mealsField.y + fontHeightInMM * index);
    });
  }

  // 餐點註記
  const mealNotesField = fields.find((f) => f.id === 'mealNotes');
  if (mealNotesField.visible && mealItems.length > 0) {
    const fontHeightInMM = pointsToMM(regularFont.heightAtSize(mealNotesField.fontSize));
    mealItems.forEach((item, index) => {
      if (item.note) {
        drawText(item.note, mealNotesField.fontSize, regularFont, mealNotesField.x, mealNotesField.y + fontHeightInMM * index);
      }
    });
  }

  // 自訂
  const customField = fields.find((f) => f.id === 'custom');
  if (customField.visible) {
    if (customField.content) {
      drawText(customField.content, customField.fontSize, regularFont, customField.x, customField.y);
    }
  }

  // 讓48x100這種可以自動裁切model輸出的時候可以往下延伸，但是資料很短的時候輸出維持直的矩形
  const maxYInMM = fields.reduce((max, field) => {
    if (field.visible) {
      return Math.max(max, field.y);
    }
    return max;
  }, 0);
  const maxYInPoints = mmToPoints(maxYInMM);
  const finalHeight = Math.max(maxYInPoints, pageWidth);
  page.setCropBox(0, height - finalHeight, pageWidth, finalHeight);
};

export const createReceiptPDF = async (orders, receiptPrinterSettings = {}, inTags, inDeliveryGroups) => {
  if (!orders || orders.length === 0) return;
  const clientId = orders[0].clientId || cache.get('app:facilityId');

  const { PDFDocument } = await import('pdf-lib');
  const fontkit = await import('pdf-fontkit');

  const pdfDoc = await PDFDocument.create();
  pdfDoc.registerFontkit(fontkit.default);

  const [regularFontResponse, boldFontResponse] = await Promise.all([
    fetch('/fonts/NotoSansTC-Regular.ttf'),
    fetch('/fonts/NotoSansTC-Bold.ttf'),
  ]);

  const [regularFontBytes, boldFontBytes] = await Promise.all([
    regularFontResponse.arrayBuffer(),
    boldFontResponse.arrayBuffer(),
  ]);

  const regularFont = await pdfDoc.embedFont(regularFontBytes, { subset: true });
  const boldFont = await pdfDoc.embedFont(boldFontBytes, { subset: true });

  const pageWidthInMM = receiptPrinterSettings.width;
  const pageHeightInMM = receiptPrinterSettings.height;
  const pageSize = [mmToPoints(pageWidthInMM), mmToPoints(pageHeightInMM)];
  const pageWidth = pageSize[0];

  const { getPropsByMode } = await import('components/DataJoinEditor/helpers');
  const { asyncListAll } = await import('utilities/graph');

  const {
    listOptionsQueryName,
    listOptionsQueryOptions,
  } = getPropsByMode('送餐對象-飲食');
  const tags = inTags || await asyncListAll(listOptionsQueryName, { ...listOptionsQueryOptions, limit: 1000 });
  const deliveryGroups = inDeliveryGroups ||
    (clientId ? await asyncListAll(getDeliveryGroupsByClientByIsActive, { clientId: clientId, isActive: { eq: 1 } }) : []);

  for (let i = 0; i < orders.length; i++) {
    const page = pdfDoc.addPage(pageSize);
    const { height } = page.getSize();
    await drawReceiptSection(page, orders[i], height, {
      regularFont,
      boldFont,
      tags,
      pageWidth,
      deliveryGroups,
      fields: receiptPrinterSettings.fields,
    });
  }

  return await pdfDoc.save();
};
